import {
  LabelVariant,
  LabelAction,
} from "@/modules/common/components/ui-kit/label";
import { LeadTag } from "@/modules/lead-tags/dtos";
import { LeadTagType } from "@/modules/lead-tags/types";
import { GetLeadResponse } from "@/modules/leads/dtos";
import { SemanticIndicatorColors } from "@/styles/nawy-theme/foundation/colors/semantic";
import { PermissionLevel, PermissionsEnum } from "@/modules/common/types";
import { userHasPermission } from "@/modules/common/utils";
import useLeadTagList from "@/modules/lead-page/hooks/lead-info/lead-tag-list.hook";
import { useCallback, useContext, useMemo } from "react";
import { NawyPassportContext } from "@/modules/common/context/nawy-passport.provider";
import { Flame, X } from "lucide-react";
import React from "react";
import { useQuery } from "@tanstack/react-query";
import { getLeadReferrals } from "@/modules/lead-page/api/lead-page.v2.api";

export interface LeadLabel {
  key: string;
  label: string | React.ReactNode;
  variant: LabelVariant;
  color: SemanticIndicatorColors;
  action?: LabelAction;
  showActionButton?: boolean;
}

export function useLeadLabels(lead: GetLeadResponse | undefined): LeadLabel[] {
  const { permissions: currentUserPermissions } =
    useContext(NawyPassportContext);
  const { removeLeadTag, isRemovingTag, removeTagVariables } = useLeadTagList();

  const { data: leadReferrals } = useQuery({
    queryKey: ["lead-referrals", lead?.data?.lead.uid],
    queryFn: () => getLeadReferrals(lead?.data?.lead.uid || ""),
    enabled: !!lead?.data?.lead.uid,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    staleTime: Infinity,
  });

  const hasTagsWritePermission = currentUserPermissions
    ? userHasPermission(
        {
          name: PermissionsEnum.LEAD_TAGS,
          level: PermissionLevel.WRITE,
        },
        currentUserPermissions
      )
    : false;

  const canManageRestricted = currentUserPermissions
    ? userHasPermission(
        {
          name: PermissionsEnum.MANAGE_RESTRICTED_LEAD_TAGS,
          level: PermissionLevel.WRITE,
        },
        currentUserPermissions
      )
    : false;

  const handleRemoveTag = useCallback(
    (tagUId: string) => {
      removeLeadTag({
        leadUId: lead?.data?.lead?.uid || "",
        leadTagUId: tagUId,
      });
    },
    [lead?.data?.lead?.uid, removeLeadTag]
  );

  const leadLabels = useMemo(() => {
    const leadTags = lead?.data?.lead?.leadTags || [];
    const showDoNotCall = lead?.data?.lead?.do_not_call;
    const showRubbish = lead?.data?.lead?.rubbish;
    const showDuplicate = lead?.data?.lead?.duplicate;
    const showArchived = lead?.data?.lead?.archived;
    const activeReferral = leadReferrals?.data.some(
      (referral) => !referral.isExpired
    );
    const showMergedChild = lead?.data?.mergedChild;
    const showMergedParent = lead?.data?.mergedParent;

    const canRemoveTag = (tag: LeadTag) =>
      hasTagsWritePermission && (!tag.isRestricted || canManageRestricted);

    return [
      ...leadTags.map((tag: LeadTag) => {
        const isHot = tag.type === LeadTagType.HOT_LEAD;
        return {
        key: tag.uid,
        label: isHot
          ? React.createElement(
              "span",
              { style: { display: "inline-flex", alignItems: "center", gap: "3px" } },
              React.createElement(Flame, { style: { width: "12px", height: "12px" } }),
              tag.name
            )
          : tag.name,
        variant: "secondary" as LabelVariant,
        color: (isHot ? "orange" : "blue") as SemanticIndicatorColors,
        action: canRemoveTag(tag)
          ? {
              icon: React.createElement(X, {
                style: { width: "14px", height: "14px" },
              }),
              onClick: () => handleRemoveTag(tag.uid),
              isLoading:
                isRemovingTag && removeTagVariables?.leadTagUId === tag.uid,
              disabled: false,
            }
          : undefined,
        showActionButton: canRemoveTag(tag),
      };
      }),
      ...(showMergedChild
        ? [
            {
              key: "merged-child",
              label: "Merged Child",
              variant: "secondary" as LabelVariant,
              color: "grey" as SemanticIndicatorColors,
            },
          ]
        : []),
      ...(showMergedParent
        ? [
            {
              key: "merged-parent",
              label: "Merged Parent",
              variant: "secondary" as LabelVariant,
              color: "grey" as SemanticIndicatorColors,
            },
          ]
        : []),
      ...(showDoNotCall
        ? [
            {
              key: "doNotCall",
              label: "Don't call",
              variant: "secondary" as LabelVariant,
              color: "red" as SemanticIndicatorColors,
            },
          ]
        : []),
      ...(showRubbish
        ? [
            {
              key: "invalid-client",
              label: "Invalid Client",
              variant: "secondary" as LabelVariant,
              color: "red" as SemanticIndicatorColors,
            },
          ]
        : []),
      ...(showDuplicate
        ? [
            {
              key: "duplicate",
              label: "Duplicate",
              variant: "secondary" as LabelVariant,
              color: "yellow" as SemanticIndicatorColors,
            },
          ]
        : []),
      ...(showArchived
        ? [
            {
              key: "archived",
              label: "Archived",
              variant: "secondary" as LabelVariant,
              color: "grey" as SemanticIndicatorColors,
            },
          ]
        : []),
      ...(activeReferral
        ? [
            {
              key: "referral",
              label: "Referral",
              variant: "secondary" as LabelVariant,
              color: "blue" as SemanticIndicatorColors,
            },
          ]
        : []),
    ];
  }, [
    lead?.data?.lead,
    leadReferrals?.data,
    handleRemoveTag,
    hasTagsWritePermission,
    canManageRestricted,
    isRemovingTag,
    removeTagVariables?.leadTagUId,
  ]);

  return leadLabels;
}
